import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { WebhookInfo } from 'common/types/api';

import { Typography, Box, Button } from '@mui/material';

import ContainerBox from 'components/ContainerBox';
import EmptyState from 'common/components/EmptyState';

import WebhooksTable from './WebhooksTable';
import CreateWebhookModal from './CreateWebhookModal';

import { useTranslation } from 'services/localization';

import * as ShopSelectors from 'selectors/ShopSelectors';
import { api } from 'common/frontend/api';
import Banner from 'components/Banner';

const Webhooks = () => {
	const { t } = useTranslation();
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [webhooks, setWebhooks] = useState<WebhookInfo[]>([]);

	const shopId = useSelector(ShopSelectors.activeShopId)!;

	useEffect(() => {
		const removeListener = api()
			.shops.doc(shopId)
			.webhooks.listenAll(
				(data: WebhookInfo[]) => {
					setWebhooks(data);
					setLoading(false);
				},
				(error) => {
					setError(true);
					setLoading(false);
				},
			);

		return () => {
			removeListener();
		};
	}, [shopId]);

	return (
		<>
			{error && (
				<Banner
					variant="danger"
					title={t(
						'Integrations.Webhooks.errorFetchingWebhooks',
						'An error occurred while fetching your webhooks. Please refresh the page.',
					)}
				/>
			)}
			<ContainerBox>
				<Box
					display="flex"
					flexDirection="row"
					alignItems="flex-end"
					justifyContent="space-between"
				>
					<Box>
						<Typography variant="h6" gutterBottom>
							{t('Integrations.Webhooks.webhooks', 'Webhooks')}
						</Typography>
						<Typography variant="body2">
							{t(
								'Integrations.Webhooks.webhooksSubtitle',
								'Manage the webhooks for your Rentle shop',
							)}
						</Typography>
					</Box>
					<Button variant="outlined" color="primary" onClick={() => setIsCreateModalOpen(true)}>
						{t('Integrations.Webhooks.createWebhook', 'Add webhook')}
					</Button>
				</Box>
				<Box my={2} />
				<EmptyState
					isLoading={loading}
					isEmpty={!webhooks?.length}
					title={t('Integrations.Webhooks.noWebhooks', 'You have no webhooks')}
					description={t(
						'Integrations.Webhooks.noWebhooksDescription',
						'Webhooks are a powerful tool for building custom integrations where external services, such as Zapier, need to react to events happening in your Rentle shop. Get started by creating your first webhook now!',
					)}
					render={() => <WebhooksTable data={webhooks} shopId={shopId} />}
				/>
				<CreateWebhookModal
					isOpen={isCreateModalOpen}
					onClose={() => setIsCreateModalOpen(false)}
					shopId={shopId}
					existingLabels={webhooks.map((wh) => wh.label)}
				/>
			</ContainerBox>
		</>
	);
};

export default Webhooks;
